import { maskedRegionProps } from "@koaform/analytics";
import { useEffect, useState } from "react";

import { api } from "../lib/api.js";

/**
 * One response, in full.
 *
 * The table truncates long answers to keep rows scannable; this is where you
 * actually read them. Same Clarity masking as the table — every value here was
 * typed by a stranger.
 */
export const ResponseDetail = ({
  formId,
  responseId,
  onClose,
  onDelete,
}: {
  formId: string;
  responseId: string;
  onClose: () => void;
  onDelete: () => void;
}) => {
  const [detail, setDetail] = useState<{
    submittedAt: string;
    answers: { fieldId: string; label: string; value: string }[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getResponse(formId, responseId)
      .then(setDetail)
      .catch(() => setError("Could not load that response."));
  }, [formId, responseId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Response"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="animate-fade fixed inset-0 z-50 flex items-center justify-center bg-ink/45 p-6 backdrop-blur-[3px]"
    >
      <div
        {...maskedRegionProps}
        className="animate-rise flex max-h-[85vh] w-[560px] max-w-full flex-col rounded-[20px] bg-surface shadow-[0_30px_70px_-20px_rgba(12,26,46,0.5)]"
      >
        <div className="flex items-start justify-between border-b border-line px-7 pt-6 pb-4">
          <div>
            <h2 className="m-0 text-[19px] font-extrabold tracking-[-0.02em]">
              Response
            </h2>
            {detail ? (
              <p className="m-0 mt-0.5 font-mono text-[11px] text-faint">
                {new Date(detail.submittedAt).toLocaleString()}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-[30px] w-[30px] flex-none rounded-lg border-0 bg-line text-[15px] text-muted hover:bg-line-input"
          >
            ×
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-7 py-5">
          {error ? (
            <p className="m-0 text-sm text-danger">{error}</p>
          ) : detail === null ? (
            <p className="m-0 text-sm text-faint">Loading…</p>
          ) : (
            <dl className="m-0 flex flex-col gap-5">
              {detail.answers.map((answer) => (
                <div key={answer.fieldId}>
                  <dt className="text-[12px] font-semibold text-body">
                    {answer.label}
                  </dt>
                  <dd className="m-0 mt-1 text-[15px] leading-[1.55] whitespace-pre-wrap text-ink">
                    {answer.value || (
                      <span className="text-faint">Not answered</span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        <div className="flex justify-between border-t border-line px-7 py-4">
          <button
            type="button"
            onClick={onDelete}
            className="rounded-[9px] border border-line-bold bg-surface px-3.5 py-2 text-sm font-semibold text-body hover:border-danger hover:text-danger"
          >
            Delete response
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[9px] bg-primary-600 px-4 py-2 text-sm font-bold text-white hover:bg-primary-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
